import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, FileText, Search } from 'lucide-react';

import { MasterLookupBar }    from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }        from '@/components/master/MasterFlash';
import { MasterAuditTab }     from '@/components/master/MasterAuditTab';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import { ChequePreview }        from '@/components/banking/ChequePreview';
import type { ChequePrintData } from '@/api/cheque.api';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Input }               from '@/components/ui/Input';
import { Badge }               from '@/components/ui/Badge';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import { Card }                from '@/components/ui/Card';
import { Combobox }            from '@/components/ui/Combobox';

import { usePermission }        from '@/hooks/usePermission';
import { useFlashMessage }    from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { Permissions }          from '@/constants/permissions';
import { AppRoutes }            from '@/constants/routes';
import { chequeLayoutApi }      from '@/api/chequeLayout.api';
import { bankApi, bankAccountApi } from '@/api/bank.api';
import type { BankListDto, BankAccountListDto } from '@/types/bank.types';
import type { ChequeLayoutDto, ChequeLayoutListDto } from '@/types/chequeLayout.types';
import type { RootState } from '@/state/store';
import { cn } from '@/utils/cn';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const optNum = z.coerce.number().nullable().optional();

const schema = z.object({
  layoutCode:        z.string().min(1, 'Layout code is required').max(20),
  // These four match their columns. The form used to allow 200/50/100/50 against varchar
  // 100/20/50/20, so a value the screen accepted came back as "An unexpected error occurred".
  layoutName:        z.string().min(1, 'Layout name is required').max(100),
  bankId:            z.string().nullable().optional(),
  bankAccountId:     z.string().nullable().optional(),
  stationeryType:    z.string().min(1, 'Stationery type is required').max(20),
  isDefault:         z.boolean(),
  isActive:          z.boolean(),
  pageWidthMm:       optNum,
  pageHeightMm:      optNum,
  payeeNameX:        optNum,
  payeeNameY:        optNum,
  payeeNameWidth:    optNum,
  amountFiguresX:    optNum,
  amountFiguresY:    optNum,
  amountFiguresWidth:optNum,
  amountWordsX:      optNum,
  amountWordsY:      optNum,
  amountWordsWidth:  optNum,
  chequeDateX:       optNum,
  chequeDateY:       optNum,
  chequeNoX:         optNum,
  chequeNoY:         optNum,
  micrZoneY:         optNum,
  signatoryLine1X:       optNum,
  signatoryLine1Y:       optNum,
  signatoryLine2X:       optNum,
  signatoryLine2Y:       optNum,
  accountPayeeTextX:     optNum,
  accountPayeeTextY:     optNum,
  voucherRefX:       optNum,
  voucherRefY:       optNum,
  rubberStampText:   z.string().max(255).nullable().optional(),
  rubberStampX:      optNum,
  rubberStampY:      optNum,
  defaultFontName:   z.string().min(1).max(50),
  defaultFontSizePt: optNum,
  amountFontSizePt:  optNum,
  dateFormat:        z.string().min(1).max(20),
  remarks:           z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  layoutCode: '', layoutName: '', bankId: null, bankAccountId: null,
  stationeryType: '', isDefault: false, isActive: true,
  pageWidthMm: null, pageHeightMm: null,
  payeeNameX: null, payeeNameY: null, payeeNameWidth: null,
  amountFiguresX: null, amountFiguresY: null, amountFiguresWidth: null,
  amountWordsX: null, amountWordsY: null, amountWordsWidth: null,
  chequeDateX: null, chequeDateY: null,
  chequeNoX: null, chequeNoY: null,
  micrZoneY: null,
  signatoryLine1X: null, signatoryLine1Y: null,
  signatoryLine2X: null, signatoryLine2Y: null,
  accountPayeeTextX: null, accountPayeeTextY: null,
  voucherRefX: null, voucherRefY: null,
  rubberStampText: null, rubberStampX: null, rubberStampY: null,
  defaultFontName: 'Arial', defaultFontSizePt: null, amountFontSizePt: null,
  dateFormat: 'dd/MM/yyyy', remarks: null,
};

const TABS = ['General', 'Print Coordinates', 'Preview', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<ChequeLayoutListDto>[] = [
  { key: 'layoutCode',     label: 'Code',           className: 'w-24 font-mono' },
  { key: 'layoutName',     label: 'Name' },
  { key: 'stationeryType', label: 'Stationery',     className: 'w-28' },
  { key: 'isDefault',      label: 'Default',        className: 'w-20',
    render: r => r.isDefault ? <Badge variant="success">Default</Badge> : null },
  { key: 'isActive',       label: 'Status',         className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: ChequeLayoutDto): FormValues {
  return {
    layoutCode: dto.layoutCode, layoutName: dto.layoutName,
    bankId: dto.bankId ?? null, bankAccountId: dto.bankAccountId ?? null,
    stationeryType: dto.stationeryType, isDefault: dto.isDefault, isActive: dto.isActive,
    pageWidthMm: dto.pageWidthMm ?? null, pageHeightMm: dto.pageHeightMm ?? null,
    payeeNameX: dto.payeeNameX ?? null, payeeNameY: dto.payeeNameY ?? null, payeeNameWidth: dto.payeeNameWidth ?? null,
    amountFiguresX: dto.amountFiguresX ?? null, amountFiguresY: dto.amountFiguresY ?? null, amountFiguresWidth: dto.amountFiguresWidth ?? null,
    amountWordsX: dto.amountWordsX ?? null, amountWordsY: dto.amountWordsY ?? null, amountWordsWidth: dto.amountWordsWidth ?? null,
    chequeDateX: dto.chequeDateX ?? null, chequeDateY: dto.chequeDateY ?? null,
    chequeNoX: dto.chequeNoX ?? null, chequeNoY: dto.chequeNoY ?? null,
    micrZoneY: dto.micrZoneY ?? null,
    signatoryLine1X: dto.signatoryLine1X ?? null, signatoryLine1Y: dto.signatoryLine1Y ?? null,
    signatoryLine2X: dto.signatoryLine2X ?? null, signatoryLine2Y: dto.signatoryLine2Y ?? null,
    accountPayeeTextX: dto.accountPayeeTextX ?? null, accountPayeeTextY: dto.accountPayeeTextY ?? null,
    voucherRefX: dto.voucherRefX ?? null, voucherRefY: dto.voucherRefY ?? null,
    rubberStampText: dto.rubberStampText ?? null,
    rubberStampX: dto.rubberStampX ?? null, rubberStampY: dto.rubberStampY ?? null,
    defaultFontName: dto.defaultFontName, defaultFontSizePt: dto.defaultFontSizePt ?? null,
    amountFontSizePt: dto.amountFontSizePt ?? null, dateFormat: dto.dateFormat,
    remarks: dto.remarks ?? null,
  };
}

/**
 * Turns the layout being edited into print data, with stand-in values.
 *
 * Sample text on purpose: a template preview is about WHERE things land, and a layout can be
 * configured before any cheque exists to render. The numbers are deliberately long — a payee of
 * "A" would sit inside any width, and the point is to see when a field is too narrow.
 */
function formToSample(f: FormValues): ChequePrintData {
  const num = (v: number | null | undefined, fallback: number) =>
    v === null || v === undefined || Number.isNaN(v) ? fallback : v;

  return {
    chequeNumber:  '000123',
    payeeName:     'Keells Food Products Ltd',
    amount:        377600,
    amountInWords: 'Rupees Three Hundred Seventy Seven Thousand Six Hundred Only',
    chequeDate:    '31/08/2026',
    accountNumber: '0072134567',
    bankName:      '',
    branchName:    '',
    currencyCode:  'LKR',
    // A cheque with no page size cannot be drawn at all; fall back to a common size rather than
    // render a zero-sized box that looks like the preview is broken.
    pageWidthMm:   num(f.pageWidthMm, 203),
    pageHeightMm:  num(f.pageHeightMm, 95),
    payeeNameX:        num(f.payeeNameX, 25),
    payeeNameY:        num(f.payeeNameY, 25),
    payeeNameWidth:    num(f.payeeNameWidth, 110),
    amountFiguresX:    num(f.amountFiguresX, 150),
    amountFiguresY:    num(f.amountFiguresY, 25),
    amountFiguresWidth:num(f.amountFiguresWidth, 40),
    amountWordsX:      num(f.amountWordsX, 25),
    amountWordsY:      num(f.amountWordsY, 38),
    amountWordsWidth:  num(f.amountWordsWidth, 140),
    chequeDateX:       num(f.chequeDateX, 150),
    chequeDateY:       num(f.chequeDateY, 12),
    chequeNoX:         num(f.chequeNoX, 20),
    chequeNoY:         num(f.chequeNoY, 78),
    defaultFontName:   f.defaultFontName || 'Arial',
    defaultFontSizePt: num(f.defaultFontSizePt, 10),
    amountFontSizePt:  num(f.amountFontSizePt, 11),
    // Shown crossed, because an account-payee cheque is the common case and the words are what
    // the A/C Payee coordinates exist to place.
    crossingText:  f.accountPayeeTextX != null && f.accountPayeeTextY != null ? 'A/C PAYEE ONLY' : null,
    crossingTextX: f.accountPayeeTextX ?? null,
    crossingTextY: f.accountPayeeTextY ?? null,
    rubberStampText: f.rubberStampText || null,
    rubberStampX:    f.rubberStampX ?? null,
    rubberStampY:    f.rubberStampY ?? null,
    payeeNameOverride: null,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ChequeLayoutPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const companyId = useSelector((s: RootState) => s.session.session?.companyId) ?? '';

  const canCreate = hasPermission(Permissions.CHEQUE_LAYOUT_CREATE);
  const canEdit   = hasPermission(Permissions.CHEQUE_LAYOUT_EDIT);
  const canDelete = hasPermission(Permissions.CHEQUE_LAYOUT_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();

  // Loaded once for the two lookups below. Small, stable reference lists — a company has a
  // handful of banks and a handful of accounts, so paging them would be ceremony for nothing.
  const [banks,    setBanks]    = useState<BankListDto[]>([]);
  const [accounts, setAccounts] = useState<BankAccountListDto[]>([]);

  const [record,       setRecord]       = useState<ChequeLayoutDto | null>(null);
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    register, handleSubmit, control, reset, watch, setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  // Watched, not read on demand: the preview's whole value is that a coordinate typed in the form
  // moves the field on the paper while you are looking at it.
  const live = watch();

  useEffect(() => {
    // A failed lookup leaves the list empty; the field then reads "no banks set up" rather than
    // blocking the screen, and every other field on the layout still works.
    bankApi.getList({ pageNumber: 1, pageSize: 200 })
      .then(r => setBanks(r.data.data?.items ?? []))
      .catch(() => setBanks([]));

    bankAccountApi.getList({ pageNumber: 1, pageSize: 200 })
      .then(r => setAccounts(r.data.data?.items ?? []))
      .catch(() => setAccounts([]));
  }, []);

  const bankId = watch('bankId');

  const bankOptions = useMemo(
    () => banks.map(b => ({ value: b.id, label: `${b.bankCode} — ${b.bankName}` })),
    [banks]);

  // Narrowed to the chosen bank: an account belongs to exactly one, and offering the others would
  // let someone build a layout that says "Bank of Ceylon" and names a Sampath account.
  const accountOptions = useMemo(
    () => accounts
      .filter(a => !bankId || a.bankId === bankId)
      .map(a => ({ value: a.id, label: `${a.accountNumber} — ${a.accountName}` })),
    [accounts, bankId]);

  const resetToInitial = useCallback(() => {
    setRecord(null); reset(EMPTY_FORM);
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: ChequeLayoutDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleEdit      = () => screen.setMode('edit');
  const handleRefresh   = () => resetToInitial();
  const handleSaveClick = () => {
    handleSubmit(
      v => { setPendingSave(v); setShowSaveConfirm(true); },
      // Without this the Save button simply did nothing whenever a field was invalid.
      formErrors => {
        const first = Object.values(formErrors)[0]?.message;
        flash('error', first ? String(first) : 'Please correct the highlighted fields.');
      },
    )();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    const mm = (v: number | null | undefined) => v ?? 0;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await chequeLayoutApi.create({
          companyId,
          layoutCode: d.layoutCode.trim().toUpperCase(),
          layoutName: d.layoutName.trim(),
          stationeryType: d.stationeryType.trim(),
          defaultFontName: d.defaultFontName.trim(),
          dateFormat: d.dateFormat.trim(),
        });
        flash('success', 'Cheque layout created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await chequeLayoutApi.update(record.id, {
          layoutName: d.layoutName.trim(),
          bankId: n(d.bankId),
          bankAccountId: n(d.bankAccountId),
          stationeryType: d.stationeryType.trim(),
          isDefault: d.isDefault,
          isActive: d.isActive,
          // The measurements below are required by the contract. A cleared box means "not
          // placed", which is what a freshly created layout already holds, so it travels as 0 —
          // sending null failed to bind and the screen had no reason to show for it.
          pageWidthMm: mm(d.pageWidthMm), pageHeightMm: mm(d.pageHeightMm),
          payeeNameX: mm(d.payeeNameX), payeeNameY: mm(d.payeeNameY), payeeNameWidth: mm(d.payeeNameWidth),
          amountFiguresX: mm(d.amountFiguresX), amountFiguresY: mm(d.amountFiguresY), amountFiguresWidth: mm(d.amountFiguresWidth),
          amountWordsX: mm(d.amountWordsX), amountWordsY: mm(d.amountWordsY), amountWordsWidth: mm(d.amountWordsWidth),
          chequeDateX: mm(d.chequeDateX), chequeDateY: mm(d.chequeDateY),
          chequeNoX: mm(d.chequeNoX), chequeNoY: mm(d.chequeNoY),
          micrZoneY: d.micrZoneY ?? null,
          signatoryLine1X: d.signatoryLine1X ?? null, signatoryLine1Y: d.signatoryLine1Y ?? null,
          signatoryLine2X: d.signatoryLine2X ?? null, signatoryLine2Y: d.signatoryLine2Y ?? null,
          accountPayeeTextX: d.accountPayeeTextX ?? null, accountPayeeTextY: d.accountPayeeTextY ?? null,
          voucherRefX: d.voucherRefX ?? null, voucherRefY: d.voucherRefY ?? null,
          rubberStampText: n(d.rubberStampText),
          rubberStampX: d.rubberStampX ?? null, rubberStampY: d.rubberStampY ?? null,
          defaultFontName: d.defaultFontName.trim(),
          defaultFontSizePt: mm(d.defaultFontSizePt),
          amountFontSizePt: mm(d.amountFontSizePt),
          dateFormat: d.dateFormat.trim(),
          remarks: n(d.remarks),
        });
        flash('success', 'Cheque layout updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const body = (err as { response?: { data?: { message?: string;
                     errors?: { message?: string }[] } } }).response?.data;
      flash('error', body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.');
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await chequeLayoutApi.remove(record.id);
      flash('success', `Layout '${record.layoutCode}' deleted.`); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handleExit = () => navigate(AppRoutes.DASHBOARD);

  const handleNewRecord = () => {
    if (!canCreate) { flash('error', 'No permission to create.'); return; }
    reset(EMPTY_FORM); setRecord(null);
    screen.setMode('new'); setActiveTab('General');
  };

  const fetchItems = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await chequeLayoutApi.getList({ companyId, search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, [companyId]);

  const handleSearchSelect = (item: ChequeLayoutListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    chequeLayoutApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load record.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  /** Binds a boolean field to the shared Toggle. */
  const ToggleField = ({ name, label, description }: { name: 'isDefault' | 'isActive'; label: string; description?: string }) => (
    <Controller name={name} control={control} render={({ field }) => (
      <Toggle
        label={label}
        description={description}
        checked={field.value as boolean}
        onChange={field.onChange}
        disabled={fieldsDisabled}
      />
    )} />
  );

  /**
   * One print coordinate, in millimetres.
   *
   * Runs at the kit's `xs` height: a layout tab is a grid of two dozen of these, and the
   * standard field height turns it into a page of scrolling.
   */
  const CoordField = ({ name, label }: { name: keyof FormValues; label: string }) => (
    <Input
      label={label}
      size="xs"
      type="number"
      step="0.1"
      {...register(name)}
      disabled={fieldsDisabled}
      placeholder="—"
    />
  );

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={FileText} title="Cheque Layout Setup"
        subtitle="Configure cheque print layouts and coordinates" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Layout"
        summary={record && (<>
            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{record.layoutCode}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.layoutName}</span>
            {record.isDefault && <Badge variant="success">Default</Badge>}
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
        </>)}
      >
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Search size={13} />}
          loading={lookupBusy}
          onClick={() => setSearchOpen(true)}
          disabled={screen.isNew || screen.isEdit || saving}
        >
          Browse
        </Button>
        {screen.isInitial && canCreate && (
          <Button variant="outline" size="sm" leftIcon={<Plus size={13} />} onClick={handleNewRecord}>
            New
          </Button>
        )}
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-4xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Layout Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Layout Code *" {...register('layoutCode')} error={errors.layoutCode?.message}
                    disabled={fieldsDisabled || screen.isEdit} placeholder="e.g. CHEQUE-A4" className="uppercase font-mono" />
                  <Input label="Layout Name *" {...register('layoutName')} error={errors.layoutName?.message}
                    disabled={fieldsDisabled} placeholder="e.g. Standard A4 Cheque" />
                  <Input label="Stationery Type *" {...register('stationeryType')} error={errors.stationeryType?.message}
                    disabled={fieldsDisabled} placeholder="e.g. A4, CHEQUE-LEAF" />
                  {/* Which stationery this layout describes. Leaving both blank makes it the
                      house default; a bank makes it that bank's; an account pins it to one.
                      Printing resolves most-specific-first. */}
                  <Controller name="bankId" control={control}
                    render={({ field }) => (
                      <Combobox
                        label="Bank"
                        options={bankOptions}
                        value={field.value ?? ''}
                        onChange={val => {
                          field.onChange(val || null);
                          // An account belonging to a different bank must not survive the change —
                          // that pairing would be a layout that contradicts itself.
                          const chosen = accounts.find(a => a.id === watch('bankAccountId'));
                          if (chosen && val && chosen.bankId !== val) setValue('bankAccountId', null);
                        }}
                        disabled={fieldsDisabled}
                        placeholder={banks.length ? 'Any bank — house default' : 'No banks set up'}
                        hint="Leave blank for the layout every other bank falls back to."
                      />
                    )}
                  />
                  <Controller name="bankAccountId" control={control}
                    render={({ field }) => (
                      <Combobox
                        label="Bank Account"
                        options={accountOptions}
                        value={field.value ?? ''}
                        onChange={val => {
                          field.onChange(val || null);
                          // Choosing an account implies its bank. Filling it in keeps the record
                          // coherent and saves a second, redundant choice.
                          const chosen = accounts.find(a => a.id === val);
                          if (chosen) setValue('bankId', chosen.bankId);
                        }}
                        disabled={fieldsDisabled}
                        placeholder={accountOptions.length
                          ? 'Any account at this bank'
                          : bankId ? 'No accounts at this bank' : 'Choose a bank first'}
                        hint="Only for an account whose stationery differs from the rest of its bank's."
                      />
                    )}
                  />
                  <Input label="Page Width (mm)" type="number" step="0.1" {...register('pageWidthMm')} disabled={fieldsDisabled} />
                  <Input label="Page Height (mm)" type="number" step="0.1" {...register('pageHeightMm')} disabled={fieldsDisabled} />
                  <Input label="Default Font Name" {...register('defaultFontName')} disabled={fieldsDisabled} placeholder="e.g. Arial" />
                  <Input label="Default Font Size (pt)" type="number" step="0.5" {...register('defaultFontSizePt')} disabled={fieldsDisabled} />
                  <Input label="Amount Font Size (pt)" type="number" step="0.5" {...register('amountFontSizePt')} disabled={fieldsDisabled} />
                  <Input label="Date Format *" {...register('dateFormat')} error={errors.dateFormat?.message}
                    disabled={fieldsDisabled} placeholder="e.g. dd/MM/yyyy" />
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2 space-y-2">
                      <ToggleField name="isDefault" label="Is Default Layout" description="Default layout is pre-selected when printing cheques." />
                      <ToggleField name="isActive" label="Is Active" />
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Textarea label="Remarks" {...register('remarks')} disabled={fieldsDisabled} rows={3} maxLength={500} placeholder="Optional notes…" />
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'Print Coordinates' && (
              <div className="space-y-5">
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">All values are in millimetres from the top-left corner of the page.</p>

                {([
                  { label: 'Payee Name',      fields: [{ n: 'payeeNameX' as const, l: 'X' }, { n: 'payeeNameY' as const, l: 'Y' }, { n: 'payeeNameWidth' as const, l: 'Width' }] },
                  { label: 'Amount (Figures)', fields: [{ n: 'amountFiguresX' as const, l: 'X' }, { n: 'amountFiguresY' as const, l: 'Y' }, { n: 'amountFiguresWidth' as const, l: 'Width' }] },
                  { label: 'Amount (Words)',   fields: [{ n: 'amountWordsX' as const, l: 'X' }, { n: 'amountWordsY' as const, l: 'Y' }, { n: 'amountWordsWidth' as const, l: 'Width' }] },
                  { label: 'Cheque Date',      fields: [{ n: 'chequeDateX' as const, l: 'X' }, { n: 'chequeDateY' as const, l: 'Y' }] },
                  { label: 'Cheque No',        fields: [{ n: 'chequeNoX' as const, l: 'X' }, { n: 'chequeNoY' as const, l: 'Y' }] },
                  { label: 'MICR Zone',        fields: [{ n: 'micrZoneY' as const, l: 'Y' }] },
                  { label: 'Signatory 1',      fields: [{ n: 'signatoryLine1X' as const, l: 'X' }, { n: 'signatoryLine1Y' as const, l: 'Y' }] },
                  { label: 'Signatory 2',      fields: [{ n: 'signatoryLine2X' as const, l: 'X' }, { n: 'signatoryLine2Y' as const, l: 'Y' }] },
                  { label: 'A/C Payee Text',   fields: [{ n: 'accountPayeeTextX' as const, l: 'X' }, { n: 'accountPayeeTextY' as const, l: 'Y' }] },
                  { label: 'Voucher Ref',      fields: [{ n: 'voucherRefX' as const, l: 'X' }, { n: 'voucherRefY' as const, l: 'Y' }] },
                  { label: 'Rubber Stamp',     fields: [{ n: 'rubberStampX' as const, l: 'X' }, { n: 'rubberStampY' as const, l: 'Y' }] },
                ] as const).map(group => (
                  <Card key={group.label}>
                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-3">{group.label}</h4>
                    <div className={cn('grid gap-3', group.fields.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
                      {group.fields.map(f => (
                        <CoordField key={f.n} name={f.n} label={f.l} />
                      ))}
                    </div>
                  </Card>
                ))}

                <Card>
                  <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                    Rubber Stamp Wording
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    The company authority block, e.g. “FOR ACME (PVT) LTD”. Leave blank when the
                    stationery is stamped by hand — nothing prints, and the print dialog's toggle
                    then has nothing to suppress.
                  </p>
                  <Input
                    type="text"
                    maxLength={255}
                    placeholder="FOR YOUR COMPANY (PVT) LTD"
                    {...register('rubberStampText')}
                  />
                </Card>
              </div>
            )}

            {activeTab === 'Preview' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Template preview
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Sample values at this layout's own measurements, so you can see where each field
                  lands before a cheque is printed. Change a coordinate on the Print Coordinates tab
                  and it moves here.
                </p>
                <div className="rounded bg-slate-100 p-3 dark:bg-slate-900/50">
                  <ChequePreview data={formToSample(live)} showGuides />
                </div>
                {(!live.pageWidthMm || !live.pageHeightMm) && (
                  <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                    No page size set on this layout — the preview falls back to a common
                    203 × 95 mm cheque so the field positions are still readable.
                  </p>
                )}
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<ChequeLayoutListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Cheque Layouts" columns={SEARCH_COLUMNS}
        fetchData={fetchItems} onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save New Layout' : 'Save Changes'}
        message={screen.isNew ? 'Save this new cheque layout?' : `Save changes to "${record?.layoutName}"?`}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Cheque Layout"
        message={<span>Delete layout <strong>{record?.layoutCode} — {record?.layoutName}</strong>?</span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};
